import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function AutomationPlatformPage() {
  const features = [
    {
      icon: 'ri-mail-send-line',
      title: 'Automated Lead Nurturing',
      description: 'Every inquiry receives immediate, personalized follow-up via email, SMS, and calls—automatically nurturing leads until they book.'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Healthcare CRM',
      description: 'Purpose-built CRM that tracks every lead, contact, and patient through your entire pipeline with full visibility and automated task management.'
    },
    {
      icon: 'ri-message-3-line',
      title: 'Multi-Channel Communication',
      description: 'Automated email sequences, SMS campaigns, and voice calls that engage leads across every channel they prefer.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Smart Appointment Booking',
      description: 'Patients can book consultations 24/7 with automated reminders, confirmations, and pre-visit preparation instructions.'
    },
    {
      icon: 'ri-user-follow-line',
      title: 'Patient Intake Workflows',
      description: 'Streamline onboarding with automated forms, document collection, insurance verification, and pre-visit questionnaires.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Pipeline Tracking',
      description: 'Real-time visibility into every lead stage with automated task assignments, follow-ups, and conversion tracking.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Missed Call Recovery',
      description: 'Automatically text or email patients who call when you\'re busy so no lead ever falls through the cracks.'
    },
    {
      icon: 'ri-star-line',
      title: 'Review Automation',
      description: 'Automatically request reviews from satisfied patients at the perfect time after their visit to build your reputation.'
    },
    {
      icon: 'ri-repeat-line',
      title: 'Re-engagement Campaigns',
      description: 'Automatically reach out to inactive patients to bring them back for follow-up care and ongoing treatment.'
    }
  ];

  const benefits = [
    {
      stat: '20+',
      label: 'Hours Saved Per Week',
      description: 'Eliminate manual follow-up and focus on patient care while automation handles lead nurturing.'
    },
    {
      stat: '3x',
      label: 'Higher Conversion Rates',
      description: 'Automated, timely follow-up converts more inquiries into booked appointments and patients.'
    },
    {
      stat: '0',
      label: 'Missed Leads',
      description: 'Every inquiry gets immediate response and consistent follow-up until they book or opt out.'
    },
    {
      stat: '24/7',
      label: 'Lead Response',
      description: 'Automated systems respond to inquiries instantly, even outside business hours and on weekends.'
    }
  ];

  const workflow = [
    {
      step: '01',
      title: 'Lead Capture',
      description: 'Patient inquires via website, phone, social media, or ads. Lead is instantly captured in the CRM.',
      icon: 'ri-user-add-line'
    },
    {
      step: '02',
      title: 'Immediate Response',
      description: 'Automated email or SMS sent within seconds with personalized greeting and next steps.',
      icon: 'ri-send-plane-line'
    },
    {
      step: '03',
      title: 'Nurture Sequence',
      description: 'Multi-touch campaign educates lead about services, answers common questions, and builds trust.',
      icon: 'ri-mail-line'
    },
    {
      step: '04',
      title: 'Booking Invitation',
      description: 'Strategic CTAs invite lead to book consultation with easy online scheduling link.',
      icon: 'ri-calendar-line'
    },
    {
      step: '05',
      title: 'Appointment Confirmed',
      description: 'Automated confirmation with appointment details, directions, and preparation instructions.',
      icon: 'ri-checkbox-circle-line'
    },
    {
      step: '06',
      title: 'Pre-Visit Automation',
      description: 'Intake forms, insurance verification, and reminders sent automatically before appointment.',
      icon: 'ri-file-list-line'
    },
    {
      step: '07',
      title: 'Post-Visit Follow-up',
      description: 'Thank you message, care instructions, review request, and next appointment scheduling.',
      icon: 'ri-heart-line'
    },
    {
      step: '08',
      title: 'Ongoing Engagement',
      description: 'Automated re-engagement campaigns keep patients coming back for ongoing care.',
      icon: 'ri-refresh-line'
    }
  ];

  const integrations = [
    'Website Forms & Chat',
    'Google Ads & Meta Ads',
    'Phone Systems',
    'Email Marketing Platforms',
    'SMS Providers',
    'Calendar & Scheduling Tools',
    'Practice Management Software',
    'Electronic Health Records (EHR)',
    'Payment Processors',
    'Review Platforms',
    'Social Media',
    'Analytics & Reporting Tools'
  ];

  const comparison = [
    {
      feature: 'Automated Lead Follow-up',
      manual: 'Inconsistent, delayed, often forgotten',
      automated: 'Instant, consistent, never missed'
    },
    {
      feature: 'Response Time',
      manual: 'Hours or days',
      automated: 'Seconds'
    },
    {
      feature: 'Lead Nurturing',
      manual: 'Sporadic, manual effort required',
      automated: 'Multi-touch sequences run automatically'
    },
    {
      feature: 'Appointment Reminders',
      manual: 'Staff calls each patient manually',
      automated: 'Automated SMS/email reminders'
    },
    {
      feature: 'No-Show Rate',
      manual: '20-30% typical',
      automated: '5-10% with automation'
    },
    {
      feature: 'Staff Time Required',
      manual: '20+ hours per week',
      automated: '2-3 hours per week'
    },
    {
      feature: 'Lead Tracking',
      manual: 'Spreadsheets, sticky notes, memory',
      automated: 'Complete CRM with full visibility'
    },
    {
      feature: 'Conversion Rate',
      manual: '10-15% typical',
      automated: '30-45% with automation'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-white/90 font-bold mb-4">
              PROPRIETARY MARKETING AUTOMATION PLATFORM
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Never Miss a Lead. Automate Every Patient Touchpoint.
            </h1>
            <p className="text-lg text-white/90 leading-relaxed mb-8">
              Our HIPAA-conscious marketing automation platform ensures every lead, contact, and patient is automatically followed up and nurtured—designed specifically for busy medical practices that can't afford to let opportunities slip away.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/book-strategy-call"
                className="inline-flex items-center gap-2 bg-white text-[#7C3AED] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
              >
                <span>Request a Platform Demo</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
              >
                <span>Explore Features</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Stats Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-8 bg-[#F9FAFB] rounded-xl hover:shadow-lg transition-all"
              >
                <div className="text-5xl font-bold text-[#7C3AED] mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-bold text-gray-900 mb-3">
                  {benefit.label}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              PLATFORM FEATURES
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Everything You Need to Automate Patient Acquisition
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              A complete marketing automation ecosystem built specifically for healthcare providers who want to grow without adding staff.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              HOW IT WORKS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              The Complete Automated Patient Journey
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              From first inquiry to loyal patient, every step is automated to maximize conversions and minimize staff workload.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflow.map((item, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-white">{item.step}</span>
                </div>
                <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-[#7C3AED] text-lg`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              MANUAL VS AUTOMATED
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              The Difference Automation Makes
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              See how marketing automation transforms your practice operations and patient acquisition results.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-3 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white p-6">
              <div className="text-sm font-bold uppercase tracking-wider">Feature</div>
              <div className="text-sm font-bold uppercase tracking-wider text-center">Manual Process</div>
              <div className="text-sm font-bold uppercase tracking-wider text-center">With Automation</div>
            </div>
            {comparison.map((item, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 p-6 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'
                }`}
              >
                <div className="font-bold text-gray-900 text-sm">{item.feature}</div>
                <div className="text-center text-gray-600 text-sm">{item.manual}</div>
                <div className="text-center text-[#7C3AED] font-bold text-sm">{item.automated}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                SEAMLESS INTEGRATIONS
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Connects With Your Existing Systems
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Our automation platform integrates with the tools you already use, creating a unified system that works together seamlessly.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {integrations.map((integration, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{integration}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/085742b88932db0814e4e79f2a1c7c25.jpg"
                  alt="Platform integrations"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HIPAA Compliance Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20data%20security%20and%20HIPAA%20compliance%20concept%20showing%20encrypted%20patient%20information%20secure%20cloud%20storage%20shield%20protection%20lock%20icons%20medical%20privacy%20safeguards%20professional%20purple%20gradient%20modern%20clean%20design&width=600&height=500&seq=hipaa1&orientation=portrait"
                  alt="HIPAA compliance"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                HIPAA-CONSCIOUS IMPLEMENTATION
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Built With Healthcare Privacy in Mind
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                We implement marketing automation with HIPAA-conscious practices, ensuring patient data is protected while maximizing your marketing effectiveness.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-[#7C3AED] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Secure Data Handling</h4>
                    <p className="text-sm text-gray-600">All patient information is encrypted and stored securely with access controls.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-line text-[#7C3AED] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Compliant Communication</h4>
                    <p className="text-sm text-gray-600">Marketing messages follow HIPAA guidelines for patient communication.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-shield-line text-[#7C3AED] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Audit Trails & Documentation</h4>
                    <p className="text-sm text-gray-600">Complete tracking of all communications and data access for compliance.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to See the Platform in Action?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Book a personalized demo and see exactly how our automation platform will transform your patient acquisition and practice growth.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#7C3AED] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Request Your Platform Demo</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>Contact Us</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
